import type { ReactElement } from 'react';
import { Palette } from 'lucide-react';
import { THEME_NAMES, type ThemeName } from '../../types.js';
import { useTheme } from '../../hooks/useTheme.js';

interface PreviewColors {
  bg: string;
  surface: string;
  accent: string;
}

const THEME_PREVIEW: Record<ThemeName, PreviewColors> = {
  'emerald-chef': { bg: '#f8fafc', surface: '#ffffff', accent: '#059669' },
  'sunset-orange': { bg: '#fffaf5', surface: '#ffffff', accent: '#ea580c' },
  'midnight-slate': { bg: '#0f172a', surface: '#1e293b', accent: '#38bdf8' },
  'berry-diet': { bg: '#fff1f2', surface: '#ffffff', accent: '#e11d48' },
  'nordic-frost': { bg: '#f0f9ff', surface: '#ffffff', accent: '#0284c7' },
  'olive-garden': { bg: '#f7fee7', surface: '#ffffff', accent: '#65a30d' },
  'espresso-cream': { bg: '#fdfbf7', surface: '#ffffff', accent: '#78350f' },
  'cyber-lime': { bg: '#0a0a0a', surface: '#171717', accent: '#84cc16' },
  'lavender-herb': { bg: '#faf5ff', surface: '#ffffff', accent: '#7c3aed' },
  'monochrome-pro': { bg: '#ffffff', surface: '#f4f4f5', accent: '#18181b' },
};

export function ThemeSection(): ReactElement {
  const { theme: current, setTheme } = useTheme();
  return (
    <section
      aria-label="Theme Selection"
      data-testid="theme-section"
      className="rounded-2xl border p-5 shadow-sm"
      style={{
        background: 'var(--bg-surface)',
        borderColor: 'var(--border)',
        color: 'var(--text-main)',
      }}
    >
      <div className="mb-4 flex items-center gap-2">
        <Palette size={20} aria-hidden="true" style={{ color: 'var(--accent)' }} />
        <h2 className="text-lg font-semibold">Theme Selection</h2>
      </div>
      <div className="grid grid-cols-5 gap-3">
        {THEME_NAMES.map((name) => {
          const preview = THEME_PREVIEW[name];
          const isActive = current === name;
          return (
            <button
              key={name}
              type="button"
              aria-label={`Theme ${name}`}
              aria-pressed={isActive}
              data-testid={`theme-swatch-${name}`}
              data-active={isActive}
              onClick={() => setTheme(name)}
              className="flex h-14 w-full items-end justify-center rounded-md border pb-1 text-[10px] font-medium"
              style={{
                background: preview.bg,
                borderColor: isActive ? 'var(--accent)' : preview.surface,
                color: preview.accent,
                boxShadow: isActive ? '0 0 0 2px var(--accent)' : 'none',
                outline: 'none',
              }}
            >
              <span aria-hidden="true" className="block h-2 w-2 rounded-full" style={{ background: preview.accent }} />
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        Active: <span className="font-medium" style={{ color: 'var(--text-main)' }}>{current}</span>
      </p>
    </section>
  );
}